import React, { ChangeEvent, useCallback, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
} from "@tanstack/react-table";

import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
  DataTableStatus,
} from "components/DataTableLoader";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { showError } from "toast/toast";
import { Download } from "lucide-react";
import { IconSquare, ViewIconButton } from "components/DatatablesActionIcons";

import {
  fetchEasyPostLabelsDatatable,
  EasyPostLabelRecord,
  downloadEasyPostLabelPdf,
  getTrackingByCode,
  type TrackingApiResponse,
} from "./Shipmentsapicalls";
import TrackShipmentModal, { type TrackData } from "./TrackShipmentModal";

/* ---------------- TYPES ---------------- */

import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";

type LabelRow = {
  id: string;
  tracking: string;
  /** Human-readable order id from API `order_id` (e.g. "00712"). */
  orderNumber: string;
  /** Mongo/order document id from API `order_database_id` — used in `/orderDetails/:id` URL. */
  orderDatabaseId?: string;
  carrier: string;
  service: string;
  amount: string;
  currency: string;
  createdAt: string;
  labelUrl: string;
};

const columnHelper = createColumnHelper<LabelRow>();

/* ================================================= */

const BuyLabels = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isSignageTeammember = location.pathname.includes("/signageteammember");
  const baseRoute = isSignageTeammember ? "/signageteammember" : "/signage";

  const { canCreate, canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.buy_labels
  );

  const token = getAuthCookie("sessionToken") || "";

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
  const [loading, setLoading] = useState(true);
  const [rowCount, setRowCount] = useState(0);
  const [rows, setRows] = useState<LabelRow[]>([]);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [trackOpen, setTrackOpen] = useState(false);
  const [activeLabel, setActiveLabel] = useState<LabelRow | null>(null);
  const fetchRequestIdRef = useRef(0);
  const hasLoadedDataRef = useRef(false);

  // Fetch labels datatable
  useEffect(() => {
    if (!token) return;

    const fetchLabels = async () => {
      const requestId = ++fetchRequestIdRef.current;
      const showInitialLoader = !hasLoadedDataRef.current;
      if (showInitialLoader) setLoading(true);
      try {
        const res = await fetchEasyPostLabelsDatatable(token, {
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
          search: globalFilter || "",
          filters: {},
          sort: { field: "created_at", order: "desc" },
        });

        const data: EasyPostLabelRecord[] = Array.isArray(res.data) ? res.data : [];
        const mapped: LabelRow[] = data.map((d) => ({
          id: String((d as any)?._id?.$oid || (d as any)?._id || ""),
          tracking: d.tracking_code || "-",
          orderNumber: d.order_id != null && String(d.order_id).trim() !== "" ? String(d.order_id) : "-",
          orderDatabaseId: d.order_database_id ? String(d.order_database_id).trim() : undefined,
          carrier: d.carrier || "-",
          service: d.service || "-",
          amount: d.postage_amount ? `$${d.postage_amount}` : "$0.00",
          currency: d.currency || "-",
          createdAt: d.created_at ? new Date(d.created_at).toLocaleString() : "-",
          labelUrl: d.label_url || "",
        }));

        if (requestId !== fetchRequestIdRef.current) return;
        setRows(mapped);
        setRowCount(Number(res.total || 0));
        hasLoadedDataRef.current = true;
      } catch (err) {
        if (requestId !== fetchRequestIdRef.current) return;
        setRows([]);
        setRowCount(0);
        showError(err instanceof Error ? err.message : "Failed to load labels");
      } finally {
        if (requestId === fetchRequestIdRef.current) {
          setLoading(false);
        }
      }
    };

    fetchLabels();
  }, [token, pagination.pageIndex, pagination.pageSize, globalFilter]);

  const formatDate = (d: string) => {
    if (!d) return "";
    try {
      const dt = new Date(d);
      return dt.toLocaleDateString(undefined, {
        weekday: "long",
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return d;
    }
  };

  const loadTrackingFor = async (row: LabelRow | null): Promise<TrackData> => {
    const tno = (row?.tracking ?? "").trim();
    if (!tno || tno === "-") {
      return {
        estimatedDeliveryTitle: "—",
        estimatedDeliverySub: "—",
        events: [{ title: "No tracking number found.", isActive: true }],
      };
    }
    if (!token) {
      return {
        estimatedDeliveryTitle: "—",
        estimatedDeliverySub: "—",
        events: [{ title: "Not authenticated.", isActive: true }],
      };
    }
    try {
      const raw = (await getTrackingByCode(token, tno)) as TrackingApiResponse;
      const details = Array.isArray(raw?.tracking_details) ? raw.tracking_details : [];
      const estDate = raw?.est_delivery_date ?? null;
      return {
        estimatedDeliveryTitle: estDate ? formatDate(estDate) || estDate : "—",
        estimatedDeliverySub: estDate ? String(estDate).slice(0, 10) : "—",
        events:
          details.length > 0
            ? details.map((e: { message?: string; status_detail?: string; status?: string; datetime?: string }, i: number) => ({
                title: String(e?.message ?? e?.status_detail ?? e?.status ?? "—"),
                dateLabel: e?.datetime ? (formatDate(e.datetime) || e.datetime) : "",
                isActive: i === 0,
              }))
            : [
                {
                  title: raw?.status_detail ?? raw?.status ?? "No tracking details yet.",
                  dateLabel: "",
                  isActive: true,
                },
              ],
      };
    } catch (e) {
      return {
        estimatedDeliveryTitle: "—",
        estimatedDeliverySub: "—",
        events: [
          { title: e instanceof Error ? e.message : "Failed to load tracking.", isActive: true },
        ],
      };
    }
  };

  const handleTrackClick = useCallback((row: LabelRow) => {
    setActiveLabel(row);
    setTrackOpen(true);
  }, []);

  const handleDownload = async (labelId: string) => {
    if (!labelId) {
      showError("Label id missing");
      return;
    }
    if (!token) {
      showError("Session expired. Please login again.");
      return;
    }
    setDownloadingId(labelId);
    try {
      const { blob, filename } = await downloadEasyPostLabelPdf(token, labelId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "label.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      showError(e instanceof Error ? e.message : "Failed to download label");
    } finally {
      setDownloadingId(null);
    }
  };

const columns = useMemo(
  () => [
    columnHelper.display({
      id: "serial",
      header: () => <div className="th-text text-center">S.No.</div>,
      cell: (info) => (
        <div className="text-sm text-center">
          {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
        </div>
      ),
    }),

    /* ---------- TRACKING NUMBER ---------- */
    columnHelper.accessor("tracking", {
      header: () => <div className="th-text text-center">TRACKING NUMBERS</div>,
      cell: (info) => {
        const row = info.row.original;
        const t = info.getValue();
        const canTrack = t && t !== "-";
        return canTrack ? (
          <div className="d-flex justify-content-center text-center">
            <button
              type="button"
              className="btn btn-link p-0  text-decoration-none  text-sm S-No order-link-1"
              style={{ fontSize: "inherit" }}
              onClick={() => handleTrackClick(row)}
            >
              {t}
            </button>
          </div>
        ) : (
          <span className="text-muted d-flex justify-content-center text-center text-sm">{t}</span>
        );
      },
    }),

    /* ---------- ORDER ID (display `order_id`, link `order_database_id`) ---------- */
    columnHelper.accessor("orderNumber", {
      header: () => <div className="th-text text-center">ORDER ID</div>,
      cell: (info) => {
        const row = info.row.original;
        const display = info.getValue() ?? "";
        const dbId = String(row.orderDatabaseId ?? "").trim();
        if (!dbId) {
          return <div className="text-sm text-center">{display}</div>;
        }
        return (
          <div className="text-sm text-center">
            <Link to={`${baseRoute}/orderDetails/${encodeURIComponent(dbId)}`} className="text-sm S-No order-link-1">
              {display}
            </Link>
          </div>
        );
      },
    }),

    /* ---------- COURIER ---------- */
    columnHelper.accessor("carrier", {
      header: () => <div className="th-text text-center">COURIER PROVIDER</div>,
      cell: (info) => <div className="text-sm text-center">{info.getValue()}</div>,
    }),

    // columnHelper.accessor("service", {
    //   header: () => <div className="th-text text-center">Service</div>,
    //   cell: (info) => <div className="text-sm text-center">{info.getValue()}</div>,
    // }),

    /* ---------- LABEL CHARGES ---------- */
    columnHelper.accessor("amount", {
      header: () => <div className="th-text text-center">LABEL CHARGES</div>,
      cell: (info) => <div className="text-sm text-center">{info.getValue()}</div>,
    }),

    // columnHelper.accessor("currency", {
    //   header: () => <div className="th-text text-center">Currency</div>,
    //   cell: (info) => <div className="text-sm text-center">{info.getValue()}</div>,
    // }),

    /* ---------- CREATED DATE ---------- */
    columnHelper.accessor("createdAt", {
      header: () => <div className="th-text text-center">CREATED ON</div>,
      cell: (info) => <div className="text-sm text-center">{info.getValue()}</div>,
    }),

    /* ---------- ACTIONS ---------- */
    columnHelper.display({
      id: "actions",
      header: () => <div className="th-text text-center">Actions</div>,
      cell: ({ row }) => (
        <div className="d-flex align-items-center justify-content-center gap-2">
          {canView && (
          <ViewIconButton
            as="button"
            type="button"
            className="icon-square"
            title="View"
            ariaLabel="View"
            onClick={() => window.open(row.original.labelUrl, "_blank", "noopener,noreferrer")}
            disabled={!row.original.labelUrl}
          />
          )}

          <IconSquare
            as="button"
            type="button"
            className="icon-square"
            title="Download"
            ariaLabel="Download"
            onClick={() => handleDownload(row.original.id)}
            disabled={!row.original.id || downloadingId === row.original.id}
          >
            <Download size={16} strokeWidth={2} aria-hidden="true" />
          </IconSquare>
        </div>
      ),
    }),
  ],
  [pagination.pageIndex, pagination.pageSize, canView]
);

  const table = useReactTable({
    data: rows,
    columns,
    state: { pagination },
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    manualPagination: true,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
  });

  const showTableLoader = loading && rows.length === 0;

  return (
    <div className="content">
      {/* Header */}
      <div className="po-header mb-4 shadow-border-datatables">
        {/* TOP ROW */}
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2">
            <h4 className="mb-1 po-title">Labels</h4>
          </div>

          {/* tools block (search + button) */}
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            <div className="input-group po-search">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search by Tracking, Order #, Carrier"
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    setPagination((p) => ({ ...p, pageIndex: 0 }));
                    setGlobalFilter(searchInput);
                  }
                }}
              />
            </div>

            {canCreate && (
              <button
              className="btn btn-primary table-addbutton fs-15"
              onClick={() => navigate(`${baseRoute}/buylabel`)}
            >
              + &nbsp; Buy Label
            </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        <div className="rdt-desktop">
          <div className="production-scroll" style={{ "--rdt-min": "1000px" } as React.CSSProperties}>
            <table className="table production-table align-middle mb-0 table-1">
              {showTableLoader ? (
                <DesktopTableLoader table={table} message="Loading labels…" />
              ) : (
                <DesktopTableParts table={table} />
              )}
            </table>
          </div>
        </div>

        <div className="rdt-mobile">
          {showTableLoader ? (
            <MobileCardsLoader message="Loading labels…" />
          ) : (
            <MobileCardsOnly
            table={table}
            titleColId="tracking"
            actionColId="actions"
          />
          )}
        </div>
      </div>

      <DataTableStatus
        loading={showTableLoader}
        isEmpty={table.getRowModel().rows.length === 0}
      >
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} />
        </div>
      </DataTableStatus>

      <TrackShipmentModal
        open={trackOpen}
        onClose={() => setTrackOpen(false)}
        trackingNumber={String(activeLabel?.tracking ?? "")}
        carrierName={String(activeLabel?.carrier ?? "")}
        initialData={null}
        loadTracking={() => loadTrackingFor(activeLabel)}
      />
    </div>
  );
};

export default BuyLabels;
